import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Check, Shield, ShieldCheck } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { API_URL } from '../lib/api';
import { buildLoginRedirectPath } from '../lib/login-redirect';
import { MilesLogo } from '../components/MilesLogo';
import { Button } from '../components/ui/button';
import { GradientBorder } from '../components/ui/gradient-border';
import { PulsingDot } from '../components/ui/pulsing-dot';

type InviteRole = 'admin' | 'member';

interface InvitePreview {
  accountId: string;
  accountName: string;
  email: string;
  role: InviteRole;
  status: string;
  expiresAt: string;
}

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const data = await res.json();
    return data.error || fallback;
  } catch {
    return fallback;
  }
}

export function TeamInvitePage() {
  const { token } = useParams({ from: '/team/invite/$token' });
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [invitation, setInvitation] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_URL}/api/v2/cloud/team/invitations/${token}`, {
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(await readError(res, 'Invite not found'));
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setInvitation(data.invitation);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Invite not found');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleAccept() {
    setAccepting(true);
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/api/v2/cloud/team/invitations/${token}/accept`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );
      if (!res.ok)
        throw new Error(await readError(res, 'Failed to accept invite'));
      setAccepted(true);
      setTimeout(() => navigate({ to: '/' }), 900);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  }

  const loginPath = buildLoginRedirectPath(`/team/invite/${token}`);
  const roleIcon =
    invitation?.role === 'admin' ? (
      <ShieldCheck className="h-4 w-4" />
    ) : (
      <Shield className="h-4 w-4" />
    );

  return (
    <div className="h-dvh flex flex-col px-4 pb-4 pt-2 gap-2">
      <div className="flex h-12 items-center">
        <Link to="/" className="inline-flex items-center gap-2">
          <MilesLogo linked={false} />
        </Link>
      </div>

      <div className="miles-container flex-1 min-h-0 flex items-center justify-center p-6">
        <GradientBorder className="w-full max-w-lg">
          <div className="p-6">
            {loading || authLoading ? (
              <div className="flex min-h-48 items-center justify-center">
                <PulsingDot />
              </div>
            ) : error && !invitation ? (
              <div>
                <h1 className="text-xl font-semibold text-white">
                  Invitation Unavailable
                </h1>
                <p className="mt-2 text-sm text-white/55">{error}</p>
                <Button asChild className="mt-6">
                  <Link to="/">Back to Sites</Link>
                </Button>
              </div>
            ) : invitation ? (
              <div>
                <div className="mb-5 inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white/70">
                  {roleIcon}
                  {invitation.role === 'admin' ? 'Admin' : 'Member'} invite
                </div>
                <h1 className="text-2xl font-semibold text-white">
                  Join {invitation.accountName}
                </h1>
                <p className="mt-3 text-sm text-white/55">
                  This invitation is for {invitation.email}. It expires on{' '}
                  {new Date(invitation.expiresAt).toLocaleDateString()}.
                </p>
                {!isAuthenticated && (
                  <p className="mt-3 text-sm text-white/45">
                    Sign in with {invitation.email} on the normal Miles login
                    page to accept this invite.
                  </p>
                )}

                {user?.email &&
                  user.email.toLowerCase() !==
                    invitation.email.toLowerCase() && (
                    <div className="mt-5 rounded-md border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-sm text-amber-200">
                      You are signed in as {user.email}. Sign in with{' '}
                      {invitation.email} to accept this invitation.
                    </div>
                  )}

                {error && (
                  <div className="mt-5 rounded-md border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                    {error}
                  </div>
                )}

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  {!isAuthenticated ? (
                    <Button asChild>
                      <a href={loginPath}>Sign In To Accept Invite</a>
                    </Button>
                  ) : accepted ? (
                    <Button disabled>
                      <Check className="h-4 w-4" />
                      Accepted
                    </Button>
                  ) : (
                    <Button onClick={handleAccept} disabled={accepting}>
                      {accepting ? 'Accepting...' : 'Accept Invite'}
                    </Button>
                  )}
                  <Button variant="ghost" asChild>
                    <Link to="/">Cancel</Link>
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </GradientBorder>
      </div>
    </div>
  );
}
